// @greenlight-spec: decisions/0026-integration-catalog-and-connection-profiles.md
// @greenlight-spec: 09-data-brokering.md
export * from './types.js';
export * from './schema.js';
export * from './placeholders.js';
export * from './registration-copy.js';
export { CATALOG_ENTRIES } from './seed.js';
export { SALESFORCE_CLIENT_CREDENTIALS_STEPS, formatAcquisitionGuideSteps, } from './salesforce-client-credentials-guide.js';
export { MICROSOFT_TEAMS_CLIENT_CREDENTIALS_STEPS } from './microsoft-teams-client-credentials-guide.js';
export { MICROSOFT_GRAPH_AGENT_INSTRUCTIONS, MICROSOFT_GRAPH_USER_STEPS, } from './microsoft-graph-user-guide.js';
export { MICROSOFT_GRAPH_APP_ONLY_AGENT_INSTRUCTIONS, MICROSOFT_GRAPH_APP_ONLY_STEPS, } from './microsoft-graph-app-only-guide.js';
export { MICROSOFT_FABRIC_SERVICE_PRINCIPAL_STEPS, FABRIC_WAREHOUSE_SERVICE_PRINCIPAL_STEPS, microsoftFabricServicePrincipalSteps, } from './microsoft-fabric-service-principal-guide.js';
export { FABRIC_WAREHOUSE_USER_AGENT_INSTRUCTIONS, FABRIC_WAREHOUSE_USER_STEPS, MICROSOFT_FABRIC_AGENT_INSTRUCTIONS, MICROSOFT_FABRIC_USER_PIECES, MICROSOFT_FABRIC_USER_PIECE_CATALOG_KEYS, MICROSOFT_FABRIC_USER_STEPS, POWER_BI_SEMANTIC_MODELS_AGENT_INSTRUCTIONS, POWER_BI_SEMANTIC_MODELS_STEPS, microsoftFabricUserAccessSteps, microsoftFabricUserSetupSteps, microsoftFabricUserSteps, } from './microsoft-fabric-user-guide.js';
export { SNOWFLAKE_AGENT_INSTRUCTIONS, SNOWFLAKE_BLOCKED_ROLES, SNOWFLAKE_SECURITY_INTEGRATION_STATEMENT, SNOWFLAKE_USER_STEPS, } from './snowflake-user-guide.js';
export { NETSUITE_M2M_STEPS } from './netsuite-m2m-guide.js';
export { GITHUB_APP_STEPS } from './github-app-guide.js';
export * from './upstream-vendors.js';
export { GOOGLE_WORKSPACE_SERVICE_ACCOUNT_STEPS } from './google-workspace-service-account-guide.js';
export { USCIS_CASE_STATUS_STEPS } from './uscis-case-status-guide.js';
export { AXIOM_API_TOKEN_STEPS } from './axiom-api-token-guide.js';
export { BOX_AGENT_INSTRUCTIONS, BOX_CCG_USER_STEPS } from './box-ccg-user-guide.js';
export { ENTRA_SIGN_IN_TEST_HINTS, OAUTH_SIGN_IN_TEST_HINTS, SIGN_IN_TEST_REDIRECT_TOKEN, SNOWFLAKE_SIGN_IN_TEST_HINTS, TEST_SIGN_IN_STEP, } from './sign-in-test-hints.js';
export { signInTestHintFor, signInTestProviderCode } from './sign-in-test-hints.js';
import { ENTRA_SIGN_IN_TEST_HINTS, OAUTH_SIGN_IN_TEST_HINTS } from './sign-in-test-hints.js';
import { connectionProfileDisplayName, deliveryModesForEntry } from './types.js';
import { CATALOG_ENTRIES } from './seed.js';
import { secretPlaceholderFor } from './registration-copy.js';
// Indexed once at module load — the catalog is a small, release-pinned, read-only
// seed, so a key lookup into this map replaces any DB join (ADR-0026 §1).
const BY_KEY = new Map(CATALOG_ENTRIES.map((e) => [e.catalogKey, e]));
/** All catalog entries, in seed order. */
export function listCatalogEntries() {
    return CATALOG_ENTRIES;
}
/** Resolve a catalog entry by its key; `undefined` for an unknown key or Custom (null). */
export function getCatalogEntry(catalogKey) {
    return catalogKey == null ? undefined : BY_KEY.get(catalogKey);
}
const ENTRA_AUTHORITY_HOSTS = new Set([
    'login.microsoftonline.com',
    'login.microsoftonline.us',
    'login.partner.microsoftonline.cn',
]);
/**
 * The hints for one per-user OAuth integration: its catalog profile's, or for a
 * Custom registration, Entra's or the generic set by its authorize URL.
 */
export function signInTestHintsFor(catalogKey, authorizeUrl) {
    const profile = getCatalogEntry(catalogKey ?? '')?.connectionProfiles.find((p) => p.authMode === 'user_delegated' && p.mechanism === 'authorization_code');
    if (profile?.signInTestHints)
        return profile.signInTestHints;
    let host = null;
    try {
        host = authorizeUrl ? new URL(authorizeUrl).hostname : null;
    }
    catch {
        host = null;
    }
    return host !== null && ENTRA_AUTHORITY_HOSTS.has(host)
        ? ENTRA_SIGN_IN_TEST_HINTS
        : OAUTH_SIGN_IN_TEST_HINTS;
}
/** The entry's pre-selected connection profile (exactly one per entry — see catalog.test.ts). */
export function defaultConnectionProfile(entry) {
    const found = entry.connectionProfiles.find((p) => p.isDefault);
    if (!found) {
        // Guarded by the seed invariant test; throw rather than silently mis-default.
        throw new Error(`Catalog entry '${entry.catalogKey}' has no default connection profile`);
    }
    return found;
}
/**
 * The connection profile that was registered, matched by `auth_mode` and, for a
 * `user_delegated` integration, its `mechanism`, so an entry with two profiles of
 * one auth mode yields the one asked for whichever is listed first. A registered
 * integration is single-mode; falls back to the default when no profile matches
 * (an IT override).
 */
export function connectionProfileForMode(entry, authMode, mechanism) {
    const sameMode = entry.connectionProfiles.filter((p) => p.authMode === authMode);
    const exact = mechanism == null ? undefined : sameMode.find((p) => (p.mechanism ?? null) === mechanism);
    return exact ?? sameMode[0] ?? defaultConnectionProfile(entry);
}
/**
 * Whether the registered profile of a catalogued integration acts as the
 * signed-in user under a mode that otherwise reads as a shared identity (Box).
 * False for Custom and for any profile without the flag.
 */
export function catalogProfileActsAsSignedInUser(catalogKey, authMode) {
    const entry = getCatalogEntry(catalogKey);
    if (entry === undefined)
        return false;
    return entry.connectionProfiles.some((p) => p.authMode === authMode && p.actsAsSignedInUser);
}
/**
 * Whether an integration's upstream sees the signed-in user rather than a shared
 * identity: every `user_delegated` integration, and a catalog profile that mints
 * per user under another mode (Box). Such an integration is proxied-only, acts as
 * the developer locally, and reads as user passthrough wherever it is shown.
 */
export function integrationActsAsSignedInUser(authMode, catalogKey) {
    return authMode === 'user_delegated' || catalogProfileActsAsSignedInUser(catalogKey, authMode);
}
/**
 * The builder-agent instructions of the catalog profile an integration was
 * registered under, matched by auth mode and mechanism. Undefined for Custom,
 * for a mode the entry does not catalogue, and for a profile without any.
 */
export function catalogAgentInstructions(catalogKey, authMode, mechanism) {
    const entry = getCatalogEntry(catalogKey);
    if (entry === undefined)
        return undefined;
    const profile = connectionProfileForMode(entry, authMode, mechanism);
    return profile.authMode === authMode ? profile.agentInstructions : undefined;
}
function fillPlaceholders(text, values) {
    if (!values)
        return text;
    return text.replace(/\{([a-z0-9_]+)\}/g, (match, key) => values[key] ?? match);
}
/**
 * Fill a setup statement's `{token}` holes with the register form's values. A token
 * with no value yet is left verbatim, so IT sees what is still missing.
 */
export function renderSetupStatement(template, values) {
    return template.replace(/\{([a-z_]+)\}/g, (match, key) => {
        const value = values[key];
        return value !== undefined && value !== '' ? value : match;
    });
}
/** The upstream security integration's name for a registration slug: an unquoted identifier. */
export function setupSecurityIntegrationName(slug) {
    const body = slug
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    return body === '' ? '' : `GREENLIGHT_${body}`;
}
function toPlaceholderDto(placeholder) {
    return {
        token: placeholder.token,
        label: placeholder.label,
        hint: placeholder.hint ?? null,
        example: placeholder.example ?? null,
        normalize: placeholder.normalize ?? null,
        format: placeholder.format ?? null,
    };
}
/**
 * Map a catalog entry to its snake_case REST DTO (ADR-0026, docs/16) — the shape the
 * registration picker reads. Shared by the server route and the demo data layer so
 * the wire shape has one source of truth. `instructionValues` fills `{placeholder}`
 * tokens in the profiles' acquisition instructions (ADR-0030 connected DB).
 */
/**
 * The builder projection of a catalog entry (ADR-0026 §7) — enough to name a system
 * in a connection-request, none of the registration mechanics. Built field by field
 * rather than by deleting keys from the full DTO, so a field added to the seed stays
 * out of a builder's reach until someone adds it here on purpose.
 */
export function toCatalogEntrySummaryDto(entry) {
    return {
        catalog_key: entry.catalogKey,
        display_name: entry.displayName,
        category: entry.category,
        logo_ref: entry.logoRef,
    };
}
export function toCatalogEntryDto(entry, instructionValues) {
    return {
        catalog_key: entry.catalogKey,
        display_name: entry.displayName,
        category: entry.category,
        base_url: entry.baseUrl,
        placeholders: (entry.placeholders ?? []).map(toPlaceholderDto),
        search_keywords: [...(entry.searchKeywords ?? [])],
        logo_ref: entry.logoRef,
        proxyable: entry.proxyable,
        engine: entry.engine ?? null,
        default_port: entry.defaultPort ?? null,
        delivery_modes: [...deliveryModesForEntry(entry)],
        probe_path: entry.probePath ?? null,
        connection_profiles: entry.connectionProfiles.map((p) => ({
            id: p.id,
            auth_mode: p.authMode,
            mechanism: p.mechanism ?? null,
            auth_inject: p.authInject ?? null,
            credential_kind: p.credentialKind,
            token_url: p.tokenUrl ?? null,
            authorize_url: p.authorizeUrl ?? null,
            token_endpoint_auth_method: p.tokenEndpointAuthMethod ?? null,
            scopes: p.scopes ? [...p.scopes] : null,
            acquisition_docs_url: p.acquisitionDocsUrl,
            acquisition_instructions: fillPlaceholders(p.acquisitionInstructions, instructionValues),
            display_name: connectionProfileDisplayName(p),
            client_id_label: p.clientIdLabel ?? null,
            client_id_placeholder: p.clientIdPlaceholder ?? null,
            secret_placeholder: secretPlaceholderFor(p.credentialKind, p.secretPlaceholder, p.tokenEndpointAuthMethod) ??
                null,
            collect_mint_scopes: p.collectMintScopes === true,
            credential_role: p.credentialRole
                ? { ...p.credentialRole, blocked: [...p.credentialRole.blocked] }
                : null,
            auth_config_fields: (p.authConfigFields ?? []).map((field) => ({
                key: field.key,
                label: field.label,
                hint: field.hint ?? null,
                example: field.example ?? null,
                format: field.format ?? null,
            })),
            acts_as_signed_in_user: p.actsAsSignedInUser === true,
            setup_guide: p.setupGuide
                ? {
                    docs_label: p.setupGuide.docsLabel,
                    steps_title: p.setupGuide.stepsTitle,
                    steps: p.setupGuide.steps.map((step) => ({
                        title: step.title,
                        body: step.body,
                        details: step.details ?? null,
                    })),
                    statement: p.setupGuide.statement ? { ...p.setupGuide.statement } : null,
                }
                : null,
            is_default: p.isDefault,
            mvp_selectable: p.mvpSelectable,
        })),
    };
}
//# sourceMappingURL=index.js.map